import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from '@mui/material';
import type { GridSize } from '@mui/material/Grid';
import { makeStyles } from '@mui/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ObjectFieldTemplateProps } from '@rjsf/utils';
import { Alert, AlertTitle } from '@mui/material';
import { useCallback } from 'react';
import { Colors } from '../../../../ui/theme/colors';

/** Vertical gap (theme spacing units) between stacked form rows; reuse for fields stacked inside custom fields. */
export const FORM_ROW_SPACING = 1.25;

export type UICollapsibleSection = {
  /** Unique id for the section. Consecutive rows sharing the same id are grouped into one collapsible section. */
  id: string;
  title: string;
  /** Whether the section is expanded when the form first renders. Defaults to false (collapsed). */
  defaultExpanded?: boolean;
};

export type UIGridElement = {
  title?: string;
  subtitle?: string;
  /** Smaller heading shown below `title` (if any), for grouping rows within a titled block. */
  subheading?: string;
  notice?: UINoticeElement;
  /** When set, the row is rendered inside a collapsible section with the other rows sharing the same section id. */
  section?: UICollapsibleSection;
  fields: {
    [key: string]: GridSize;
  };
};

export type UINoticeElement = {
  isEnabledPredicate?: (formData: Record<string, any>) => boolean;
  title?: string;
  notice: string;
  severity: 'error' | 'warning' | 'info' | 'success';
};

type UIGridRowGroup = {
  section?: UICollapsibleSection;
  rows: { row: UIGridElement; key: number }[];
};

const useStyles = makeStyles({
  formGrid: {
    '& input::placeholder, & textarea::placeholder': {
      color: Colors.midLightGray,
      opacity: 1
    }
  }
});

function groupRowsBySection(gridConfig: UIGridElement[]): UIGridRowGroup[] {
  const groups: UIGridRowGroup[] = [];
  gridConfig.forEach((row, index) => {
    const lastGroup = groups[groups.length - 1];
    if (row.section && lastGroup?.section?.id === row.section.id) {
      lastGroup.rows.push({ row, key: index });
    } else {
      groups.push({ section: row.section, rows: [{ row, key: index }] });
    }
  });
  return groups;
}

// Custom ObjectFieldTemplate for layout customization
export const CustomObjectFieldTemplate: React.FC<ObjectFieldTemplateProps> = ({ properties, uiSchema, formData }) => {
  const classes = useStyles();
  const gridConfig: UIGridElement[] = (uiSchema as any)['ui:grid'] || [];

  // Check if the notice should be displayed
  const isNoticeEnabled = useCallback(
    (notice: UINoticeElement) => {
      if (!formData || !notice.isEnabledPredicate) {
        return false;
      }
      return notice.isEnabledPredicate(formData);
    },
    [formData]
  );

  const renderRow = (row: UIGridElement, key: number) => (
    <Grid container spacing={2} key={key} sx={{ mb: FORM_ROW_SPACING }}>
      {row.title && (
        <Grid style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }} size={12}>
          <Typography variant="h6">{row.title}</Typography>
          {row.subtitle && (
            <Typography variant="body2" style={{ color: Colors.midGray }}>
              {row.subtitle}
            </Typography>
          )}
        </Grid>
      )}

      {row.subheading && (
        <Grid size={12}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {row.subheading}
          </Typography>
        </Grid>
      )}

      {row.notice && isNoticeEnabled(row.notice) && (
        <Grid size={12}>
          <Alert severity={row.notice.severity}>
            {row.notice.title && <AlertTitle>{row.notice.title}</AlertTitle>}
            {row.notice.notice}
          </Alert>
        </Grid>
      )}

      {Object.entries(row.fields).map(([fieldName, gridSize]) => {
        const prop = properties.find((p) => p.name === fieldName);
        return (
          prop && (
            <Grid key={prop.name} size={gridSize}>
              {prop.content}
            </Grid>
          )
        );
      })}
    </Grid>
  );

  return (
    <Grid container spacing={2} className={classes.formGrid}>
      {groupRowsBySection(gridConfig).map((group) =>
        group.section ? (
          <Grid key={`section-${group.section.id}`} sx={{ mb: FORM_ROW_SPACING }} size={12}>
            <Accordion defaultExpanded={group.section.defaultExpanded ?? false} variant="outlined">
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${group.section.id}-content`}
                id={`${group.section.id}-header`}
              >
                <Typography variant="h6">{group.section.title}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {group.rows.map(({ row, key }) => renderRow(row, key))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        ) : (
          group.rows.map(({ row, key }) => renderRow(row, key))
        )
      )}
    </Grid>
  );
};
